'use client';

import * as React from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

export interface ForecastAreaPoint {
	label: string;
	production: number;
	consumption: number;
}

/** 7-day production vs consumption forecast areas. */
const ForecastAreaChart = ({ data }: { data: ForecastAreaPoint[] }) => (
	<ResponsiveContainer width="100%" height={120}>
		<AreaChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
			<defs>
				<linearGradient id="gFP7" x1="0" y1="0" x2="0" y2="1">
					<stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
					<stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
				</linearGradient>
				<linearGradient id="gFC7" x1="0" y1="0" x2="0" y2="1">
					<stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.2} />
					<stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0} />
				</linearGradient>
			</defs>
			<XAxis
				dataKey="label"
				tick={{ fontSize: 11, fill: '#64748b' }}
				tickLine={false}
				axisLine={false}
			/>
			<YAxis
				tick={{ fontSize: 11, fill: '#64748b' }}
				tickLine={false}
				axisLine={false}
				unit=" kWh"
				width={50}
			/>
			<Tooltip
				contentStyle={{
					background: '#0f1f35',
					border: '1px solid #1a3a5c',
					borderRadius: 8,
					fontSize: 13,
				}}
				labelStyle={{ color: '#e2e8f0' }}
			/>
			<Area
				type="monotone"
				dataKey="production"
				name="Produzione FV"
				stroke="#f59e0b"
				fill="url(#gFP7)"
				strokeWidth={2}
			/>
			<Area
				type="monotone"
				dataKey="consumption"
				name="Consumo configurazione"
				stroke="var(--color-accent)"
				fill="url(#gFC7)"
				strokeWidth={1.5}
				strokeDasharray="4 2"
			/>
		</AreaChart>
	</ResponsiveContainer>
);
ForecastAreaChart.displayName = 'ForecastAreaChart';

export interface HourlyForecastPoint {
	ora: string;
	produzione: number;
	consumo: number;
}

/** Hourly production profile for one day, optional consumption overlay. */
const HourlyForecastChart = ({
	data,
	showConsumo,
	unit = ' kWh',
}: {
	data: HourlyForecastPoint[];
	showConsumo?: boolean;
	/** Unità dell'asse Y — i valori sono energia nell'ora, non potenza istantanea. */
	unit?: string;
}) => (
	<ResponsiveContainer width="100%" height={90}>
		<AreaChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -15 }}>
			<defs>
				<linearGradient id="gHP" x1="0" y1="0" x2="0" y2="1">
					<stop offset="0%" stopColor="#f59e0b" stopOpacity={0.5} />
					<stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
				</linearGradient>
			</defs>
			<XAxis
				dataKey="ora"
				tick={{ fontSize: 10, fill: '#64748b' }}
				tickLine={false}
				axisLine={false}
				interval={2}
			/>
			<YAxis
				tick={{ fontSize: 10, fill: '#64748b' }}
				tickLine={false}
				axisLine={false}
				unit={unit}
				width={40}
			/>
			<Tooltip
				contentStyle={{
					background: '#0f1f35',
					border: '1px solid #1a3a5c',
					borderRadius: 8,
					fontSize: 13,
				}}
				labelStyle={{ color: '#e2e8f0' }}
			/>
			<Area
				type="monotone"
				dataKey="produzione"
				name="Produzione"
				stroke="#f59e0b"
				fill="url(#gHP)"
				strokeWidth={2}
			/>
			{showConsumo && (
				<Area
					type="monotone"
					dataKey="consumo"
					name="Consumo"
					stroke="var(--color-accent)"
					fill="none"
					strokeWidth={1.5}
					strokeDasharray="3 2"
				/>
			)}
		</AreaChart>
	</ResponsiveContainer>
);
HourlyForecastChart.displayName = 'HourlyForecastChart';

export { ForecastAreaChart, HourlyForecastChart };
